import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Pencil, Printer, ClipboardList, PackagePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FormModal, Field, SectionTitle, StockTypeTabs } from "@/pages/InwardStock";

const PO_STATUSES = ["Draft", "Sent", "Confirmed", "Partially Received", "Closed", "Cancelled"];
const CATEGORIES = ["E-Rickshaw", "Solar", "Automotive", "Inverter", "Custom"];

const statusClass = (s) => {
  if (s === "Closed") return "badge-ok";
  if (s === "Cancelled") return "badge-danger";
  if (s === "Draft") return "bg-zinc-100 text-zinc-600 border border-zinc-300 rounded-full px-2 py-0.5 text-[11px] font-bold";
  return "bg-[#E3F0FF] text-[#145B98] border border-[#B8D6F0] rounded-full px-2 py-0.5 text-[11px] font-bold";
};

export default function PurchaseOrders() {
  const { user } = useAuth();
  const [stockType, setStockType] = useState("ready");
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [show, setShow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty("ready"));

  function empty(st) {
    return {
      stock_type: st || "ready",
      vendor_id: "", date: new Date().toISOString().slice(0, 10), expected_date: "",
      status: "Draft", category: "E-Rickshaw", item: "", model: "", quantity: 1, rate: 0, tax_percent: 0, notes: "",
    };
  }

  const load = async () => {
    const [p, v] = await Promise.all([api.get(`/purchase-orders?stock_type=${stockType}`), api.get("/vendors")]);
    setItems(p.data); setVendors(v.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stockType]);

  const close = () => { setShow(false); setEditingId(null); setForm(empty(stockType)); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        stock_type: form.stock_type || stockType,
        quantity: parseInt(form.quantity),
        rate: parseFloat(form.rate),
        tax_percent: parseFloat(form.tax_percent || 0),
      };
      if (editingId) { await api.patch(`/purchase-orders/${editingId}`, payload); toast.success("PO updated"); }
      else { await api.post("/purchase-orders", payload); toast.success("PO created"); }
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (x) => {
    setEditingId(x.id);
    setForm({
      stock_type: x.stock_type || "ready",
      vendor_id: x.vendor_id, date: x.date, expected_date: x.expected_date || "", status: x.status,
      category: x.category || "E-Rickshaw", item: x.item || "", model: x.model || "",
      quantity: x.quantity, rate: x.rate, tax_percent: x.tax_percent, notes: x.notes || "",
    });
    setShow(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this Purchase Order?")) return;
    try { await api.delete(`/purchase-orders/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const convert = async (x) => {
    if (x.status === "Closed") { toast.error("PO already converted"); return; }
    const batch = window.prompt(`Convert PO ${x.po_number} to Inward Stock.\n\nEnter batch number for the received goods:`, `${x.po_number}`);
    if (!batch) return;
    try {
      await api.post(`/purchase-orders/${x.id}/convert-to-inward`, { batch_number: batch });
      toast.success(`Converted to Inward. Batch: ${batch}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
  };

  const print = (x) => printOrder({
    title: "PURCHASE ORDER", number: x.po_number, date: x.date, party: x.vendor_name,
    partyLabel: "Vendor", extraDateLabel: "Expected Delivery", extraDate: x.expected_date,
    item: x.item, model: x.model, category: x.category, quantity: x.quantity, rate: x.rate,
    tax_percent: x.tax_percent, total: x.total, notes: x.notes, status: x.status,
  });

  const total = (parseFloat(form.rate || 0) * parseInt(form.quantity || 0)) * (1 + parseFloat(form.tax_percent || 0) / 100);

  return (
    <div className="p-8 space-y-6" data-testid="po-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#28B516] mr-2 align-middle" />Procurement · {stockType === "raw" ? "Raw Material" : "Ready Stock"}</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Purchase Orders</h1>
        </div>
        <button onClick={() => { setForm(empty(stockType)); setShow(true); }} className="btn-primary flex items-center gap-2" data-testid="new-po-btn"><Plus className="w-4 h-4" /> New Purchase Order</button>
      </div>

      <StockTypeTabs value={stockType} onChange={setStockType} testidPrefix="po" />

      <div className="border border-zinc-200 bg-white rounded-2xl overflow-x-auto">
        <table className="data-table" data-testid="po-table">
          <thead><tr><th>PO No.</th><th>Date</th><th>Vendor</th><th>Item</th><th className="text-right">Qty</th><th className="text-right">Total ₹</th><th>Expected</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="9" className="text-center py-12 text-zinc-500"><ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-40" />No purchase orders yet.</td></tr>}
            {items.map((x) => (
              <tr key={x.id}>
                <td className="font-mono font-bold text-[#28B516]">{x.po_number}</td>
                <td>{x.date}</td>
                <td className="font-semibold">{x.vendor_name}</td>
                <td>{x.item}<div className="text-xs text-zinc-500">{x.model}</div></td>
                <td className="text-right font-mono">{x.quantity}</td>
                <td className="text-right font-mono font-bold text-[#28B516]">{x.total.toLocaleString()}</td>
                <td>{x.expected_date || "—"}</td>
                <td><span className={statusClass(x.status)}>{x.status}</span></td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => print(x)} className="text-zinc-400 hover:text-[#1769AA] transition-colors" title="Print" data-testid={`print-po-${x.id}`}><Printer className="w-4 h-4" /></button>
                    {x.status !== "Closed" && x.status !== "Cancelled" && (user?.role === "admin" || x.created_by === user?.email) && (
                      <button onClick={() => convert(x)} className="text-zinc-400 hover:text-[#28B516] transition-colors" title="Convert to Inward Stock" data-testid={`convert-po-${x.id}`}><PackagePlus className="w-4 h-4" /></button>
                    )}
                    {(user?.role === "admin" || x.created_by === user?.email) && (
                      <>
                        <button onClick={() => startEdit(x)} className="text-zinc-400 hover:text-[#28B516] transition-colors" title="Edit" data-testid={`edit-po-${x.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(x.id)} className="text-zinc-400 hover:text-[#D9363E] transition-colors" title="Delete" data-testid={`del-po-${x.id}`}><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <FormModal onClose={close}>
          <form onSubmit={submit} className="space-y-5" data-testid="po-form">
            <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
              <h2 className="font-display text-2xl uppercase text-zinc-900">{editingId ? "Edit Purchase Order" : "New Purchase Order"}</h2>
              <button type="button" onClick={close} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>
            <SectionTitle>Order Details</SectionTitle>
            <div className="flex gap-2 mb-2" data-testid="po-stock-type">
              {[{ k: "ready", label: "Ready Stock" }, { k: "raw", label: "Raw Material" }].map((o) => (
                <button
                  type="button"
                  key={o.k}
                  onClick={() => setForm({ ...form, stock_type: o.k })}
                  data-testid={`po-stock-type-${o.k}`}
                  className={`px-4 py-2 border text-xs uppercase font-bold tracking-widest transition-colors ${
                    form.stock_type === o.k ? "bg-[#28B516] border-[#28B516] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-[#28B516] hover:text-[#28B516]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="PO Date*"><input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-field" data-testid="po-date" /></Field>
              <Field label="Vendor*">
                <select required value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} className="input-field" data-testid="po-vendor">
                  <option value="">Select vendor…</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-field" data-testid="po-status">
                  {PO_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Expected Delivery"><input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} className="input-field" data-testid="po-expected" /></Field>
              <Field label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-field" data-testid="po-category">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Item*"><input required value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} className="input-field" data-testid="po-item" /></Field>
              <Field label="Model"><input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="input-field" data-testid="po-model" /></Field>
              <Field label="Quantity*"><input required type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="input-field font-mono" data-testid="po-qty" /></Field>
              <Field label="Rate ₹*"><input required type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="input-field font-mono" data-testid="po-rate" /></Field>
              <Field label="Tax %"><input type="number" step="0.01" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} className="input-field font-mono" data-testid="po-tax" /></Field>
              <Field label="Total (auto)"><div className="input-field font-mono text-[#28B516] font-bold" data-testid="po-total">₹{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div></Field>
              <Field label="Notes / Terms" full><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field h-20 resize-none" data-testid="po-notes" /></Field>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={close} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-po">{editingId ? "Update PO" : "Create PO"}</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
}

export function printOrder(o) {
  const html = `<!doctype html><html><head><title>${o.title} ${o.number}</title>
<style>body{font-family:Inter,Arial,sans-serif;padding:40px;color:#18231D}h1{color:#28B516;margin:0}
.head{display:flex;justify-content:space-between;border-bottom:3px solid #28B516;padding-bottom:16px;margin-bottom:24px}
.box{border:1px solid #DCE6DF;background:#F4F7F5;padding:14px 16px;border-radius:10px;margin:16px 0}
table{width:100%;border-collapse:collapse;margin-top:16px}
th,td{border:1px solid #DCE6DF;padding:12px;text-align:left}th{background:#F2F7F3;color:#536057;font-size:12px;text-transform:uppercase;letter-spacing:.08em}
.total{text-align:right;font-size:20px;font-weight:800;color:#28B516;margin-top:24px}
.mini{color:#6B786F;font-size:12px}.right{text-align:right}
.sig{margin-top:60px;border-top:1px solid #DCE6DF;padding-top:8px;width:220px;font-size:12px;color:#536057}
.badge{display:inline-block;padding:4px 10px;border-radius:14px;font-size:11px;font-weight:800;background:#E3F0FF;color:#145B98}
</style></head><body>
<div class="head">
  <div><h1>GREENRGY POWER</h1><div class="mini">Lithium Battery Management</div></div>
  <div class="right"><h2 style="margin:0">${o.title}</h2><div style="font-size:22px;font-weight:800;color:#28B516">${o.number}</div>
  <div class="mini">Date: ${o.date}${o.extraDate ? ` &middot; ${o.extraDateLabel}: ${o.extraDate}` : ""}</div>
  <div style="margin-top:8px"><span class="badge">${o.status}</span></div></div>
</div>
<div class="box"><b>${o.partyLabel}:</b> ${o.party || "—"}${o.category ? `<br><span class="mini">Category: ${o.category}</span>` : ""}</div>
<table><thead><tr><th>Item</th><th>Model</th><th class="right">Qty</th><th class="right">Rate ₹</th><th class="right">Tax %</th><th class="right">Total ₹</th></tr></thead>
<tbody><tr><td>${o.item || "—"}</td><td>${o.model || "—"}</td><td class="right">${o.quantity}</td>
<td class="right">${(o.rate || 0).toLocaleString()}</td><td class="right">${o.tax_percent || 0}%</td>
<td class="right"><b>${(o.total || 0).toLocaleString()}</b></td></tr></tbody></table>
${o.notes ? `<div class="box"><b>Notes / Terms</b><br>${o.notes.replace(/</g, "&lt;")}</div>` : ""}
<div class="total">Grand Total: ₹${(o.total || 0).toLocaleString()}</div>
<div class="sig">Authorized Signatory</div>
<script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script>
</body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}
